import * as React from 'react';

type UIContextProviderComponent<T> = React.FC<{
  value: T;
  children?: React.ReactNode;
}>;

/**
 * Returns [Provider, useHook, Context] where useHook throws if used outside Provider.
 */
export function createUIContext<T>(name: string): [UIContextProviderComponent<T>, () => T, React.Context<T | undefined>] {
  const Context = React.createContext<T | undefined>(undefined);
  Context.displayName = name;

  const Provider: UIContextProviderComponent<T> = ({ value, children }) =>
    React.createElement(Context.Provider, { value }, children);
  Provider.displayName = `${name}Provider`;

  function useContextValue(): T {
    const ctx = React.useContext(Context);
    if (ctx === undefined) {
      throw new Error(`${name} must be used within a ${name}Provider`);
    }
    return ctx;
  }

  return [Provider, useContextValue, Context];
}
